/**
 * One host's page: its status card beside its instruction history. The home page
 * does not repeat that list — activity there is only the in-flight / last-failed
 * banners — so this is where an operator comes to scroll what the machine has been
 * doing. The two hosts are not interchangeable — one prepares a release, one deploys
 * it — so each has its own tab and its own page rather than sharing a "Hosts" screen,
 * which let an operator reach for the release host while looking at the prepare
 * host's status.
 */
import { Navigate, useParams } from 'react-router-dom';
import { Grid, Paper, Stack, Typography } from '@mui/material';
import type { HostRole } from '@finvari/deployer';
import { HostCard } from '../components/HostCard';
import { InstructionList } from '../components/InstructionList';

const ROLES: HostRole[] = ['prepare', 'release'];

function isRole(value: string | undefined): value is HostRole {
    return ROLES.includes(value as HostRole);
}

export function HostPage() {
    const { role } = useParams();
    // An unknown role in the URL is a dead link, not a host — send it home rather
    // than render an empty card for a machine that does not exist.
    if (!isRole(role)) return <Navigate to="/" replace />;

    return (
        <Stack spacing={4}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <HostCard role={role} />
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            {role} instructions
                        </Typography>
                        <InstructionList role={role} limit={25} initialVisible={6} />
                    </Paper>
                </Grid>
            </Grid>
        </Stack>
    );
}
